import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Video,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  ExternalLink,
  Copy,
  ListChecks,
  Mail,
  Phone,
  User,
} from 'lucide-react';
import { useToastContext } from '../../contexts/ToastContext';
import {
  getConsultations,
  acceptConsultation,
  rejectConsultation,
  completeConsultation,
} from '../../services/consultationService';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import ModalShell from '../../components/shared/ModalShell';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const STATUS_TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'completed', label: 'Completed' },
  { key: 'rejected', label: 'Rejected' },
  { key: '', label: 'All' },
];

const normalizeMeetingLink = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/+/, '')}`;
};

const getApiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (data?.message) return data.message;
  const errors = data?.errors;
  if (errors && typeof errors === 'object') {
    const first = Object.values(errors).flat().find(Boolean);
    if (first) return String(first);
  }
  return fallback;
};

const getStatusBadge = (status) => {
  const styles = {
    pending:
      'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/50',
    accepted:
      'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/50',
    rejected:
      'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800/50',
    completed:
      'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/50',
  };
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium capitalize border ${
        styles[status] ||
        'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
      }`}
    >
      {status}
    </span>
  );
};

const VendorConsultations = () => {
  const { toast } = useToastContext();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [acceptingId, setAcceptingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [showAcceptModal, setShowAcceptModal] = useState(null);
  const [acceptedMeetingLink, setAcceptedMeetingLink] = useState(null);
  const [meetingLink, setMeetingLink] = useState('');
  const [meetingLinkError, setMeetingLinkError] = useState('');
  const [vendorNotes, setVendorNotes] = useState('');

  const params = statusFilter ? { status: statusFilter } : {};
  const { data: consultationsData, isLoading } = useQuery({
    queryKey: ['vendorConsultations', params],
    queryFn: () => getConsultations(params),
  });

  const consultations = consultationsData?.data?.data || consultationsData?.data || [];

  const acceptMutation = useMutation({
    mutationFn: ({ id, data }) => acceptConsultation(id, data),
    onSuccess: (res) => {
      const link = res?.data?.google_meet_link;
      if (link) {
        setAcceptedMeetingLink(link);
      } else {
        toast.success(null, 'Consultation accepted. The meeting link will be sent to the client.');
        closeAcceptModal();
      }
      queryClient.invalidateQueries({ queryKey: ['vendorConsultations'] });
    },
    onError: (err) => {
      const message = getApiErrorMessage(err, 'Failed to accept consultation');
      const linkError = err?.response?.data?.errors?.google_meet_link?.[0];
      if (linkError) {
        setMeetingLinkError(linkError);
      }
      toast.error('Could not accept consultation', message);
    },
    onSettled: () => setAcceptingId(null),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, data }) => rejectConsultation(id, data),
    onSuccess: () => {
      toast.success(null, 'Consultation rejected');
      queryClient.invalidateQueries({ queryKey: ['vendorConsultations'] });
    },
    onError: (err) => {
      toast.error(null, err?.response?.data?.message || 'Failed to reject consultation');
    },
    onSettled: () => setRejectingId(null),
  });

  const completeMutation = useMutation({
    mutationFn: (id) => completeConsultation(id),
    onSuccess: async () => {
      toast.success(null, 'Consultation marked as completed');
      await queryClient.refetchQueries({ queryKey: ['vendorConsultations'] });
    },
    onError: (err) => {
      toast.error(null, err?.response?.data?.message || 'Failed to complete consultation');
    },
    onSettled: () => setCompletingId(null),
  });

  const handleAccept = (consultation) => {
    const normalizedLink = normalizeMeetingLink(meetingLink);
    if (!normalizedLink) {
      setMeetingLinkError('Please enter a meeting link (Zoom, Google Meet, Teams, etc.)');
      toast.error('Meeting link required', 'Add a full link starting with https://');
      return;
    }

    try {
      // eslint-disable-next-line no-new
      new URL(normalizedLink);
    } catch {
      setMeetingLinkError(
        'Enter a valid URL (e.g. https://zoom.us/j/123 or https://meet.google.com/abc-defg-hij)'
      );
      toast.error('Invalid meeting link', 'Use a full https:// link for Zoom, Meet, Teams, or similar.');
      return;
    }

    setMeetingLinkError('');
    setAcceptingId(consultation.id);
    acceptMutation.mutate({
      id: consultation.id,
      data: {
        google_meet_link: normalizedLink,
        vendor_notes: vendorNotes || null,
      },
    });
  };

  const closeAcceptModal = () => {
    setShowAcceptModal(null);
    setAcceptedMeetingLink(null);
    setMeetingLink('');
    setMeetingLinkError('');
    setVendorNotes('');
  };

  const handleCopyLink = () => {
    if (acceptedMeetingLink) {
      navigator.clipboard.writeText(acceptedMeetingLink);
      toast.success(null, 'Link copied to clipboard');
    }
  };

  const handleComplete = (consultation) => {
    if (!window.confirm('Mark this consultation as completed?')) return;
    setCompletingId(consultation.id);
    completeMutation.mutate(consultation.id);
  };

  const handleReject = (consultation) => {
    if (!window.confirm('Are you sure you want to reject this consultation request?')) return;
    setRejectingId(consultation.id);
    rejectMutation.mutate({ id: consultation.id, data: {} });
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : 'TBD';
  const formatTime = (t) => {
    if (!t) return 'TBD';
    const parts = String(t).split(':');
    const h = parseInt(parts[0] || 0, 10);
    const m = parts[1] || '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${h12}:${m} ${ampm}`;
  };

  const emptyCopy = statusFilter
    ? `No ${statusFilter} consultations right now.`
    : 'When clients book a consultation from your services, their requests will show up here.';

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Consultations"
        subtitle="Accept, decline, or complete client consultation requests before they book."
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.consultations.title}
        steps={VENDOR_PAGE_GUIDES.consultations.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-3 sm:px-5">
          <div
            className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
            role="tablist"
            aria-label="Consultation status"
          >
            {STATUS_TABS.map((tab) => {
              const isActive = statusFilter === tab.key;
              return (
                <button
                  key={tab.key || 'all'}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setStatusFilter(tab.key)}
                  className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading consultations…</p>
            </div>
          ) : consultations.length === 0 ? (
            <div className="py-16 sm:py-20 text-center px-4">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400">
                <Video className="w-7 h-7" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
                No consultations
              </h3>
              <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto leading-relaxed">
                {emptyCopy}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {consultations.map((c) => {
                const clientName =
                  c.client?.name || c.client_name || c.client_display_name || 'Guest Client';
                const email = c.client?.email || c.client_email || c.client_display_email;
                const phone = c.client?.phone || c.client_phone || c.client_display_phone;
                const isGuest = !c.client && !c.client_id;

                return (
                  <div
                    key={c.id}
                    className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4 sm:p-5 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                      <div className="flex-1 min-w-0 space-y-2.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-50 truncate">
                            {clientName}
                          </h3>
                          {isGuest && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300">
                              <User className="w-3 h-3" />
                              Guest
                            </span>
                          )}
                          {getStatusBadge(c.status)}
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-gray-600 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            {formatDate(c.scheduled_date)}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            {formatTime(c.scheduled_time)}
                          </span>
                          {c.service?.service_name && (
                            <span className="font-medium text-gray-800 dark:text-gray-200">
                              {c.service.service_name}
                            </span>
                          )}
                        </div>

                        {(email || phone) && (
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                            {email && (
                              <span className="inline-flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5 shrink-0" />
                                {email}
                              </span>
                            )}
                            {phone && (
                              <span className="inline-flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 shrink-0" />
                                {phone}
                              </span>
                            )}
                          </div>
                        )}

                        {c.questionnaire_responses &&
                          Object.keys(c.questionnaire_responses).length > 0 && (
                            <details className="mt-1 group">
                              <summary className="text-sm font-medium text-primary-700 dark:text-primary-300 cursor-pointer list-none inline-flex items-center gap-1 hover:underline">
                                View client responses
                              </summary>
                              <ul className="mt-2 space-y-1.5 text-sm text-gray-600 dark:text-gray-400 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
                                {Object.entries(c.questionnaire_responses).map(([k, v]) =>
                                  v ? (
                                    <li key={k} className="leading-snug">
                                      <span className="font-medium text-gray-800 dark:text-gray-200">
                                        {k}:
                                      </span>{' '}
                                      {v}
                                    </li>
                                  ) : null
                                )}
                              </ul>
                            </details>
                          )}
                      </div>

                      {c.status === 'pending' && (
                        <div className="flex flex-wrap gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="primary"
                            className="!rounded-lg"
                            onClick={() => setShowAcceptModal(c)}
                            disabled={acceptingId || rejectingId}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="!rounded-lg text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-950/30"
                            onClick={() => handleReject(c)}
                            disabled={acceptingId || rejectingId}
                            loading={rejectMutation.isPending && rejectingId === c.id}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}

                      {c.status === 'accepted' && (
                        <div className="flex flex-wrap items-center gap-2 shrink-0">
                          {c.google_meet_link && (
                            <a
                              href={c.google_meet_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 dark:text-primary-300 hover:underline"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Join meeting
                            </a>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="!rounded-lg"
                            onClick={() => handleComplete(c)}
                            disabled={completingId || acceptingId || rejectingId}
                            loading={completeMutation.isPending && completingId === c.id}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Mark completed
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ModalShell
        isOpen={Boolean(showAcceptModal)}
        onClose={closeAcceptModal}
        labelledBy="accept-consultation-title"
        panelClassName="max-w-md"
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-5 sm:p-6">
          {acceptedMeetingLink ? (
            <>
              <div className="flex items-center gap-2.5 mb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle className="w-5 h-5" />
                </div>
                <h3
                  id="accept-consultation-title"
                  className="text-lg font-semibold text-gray-900 dark:text-gray-50"
                >
                  Consultation accepted
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                The client will receive this meeting link by email. You can also share it directly.
              </p>
              <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-950/50 rounded-xl border border-gray-200 dark:border-gray-700 mb-5">
                <a
                  href={acceptedMeetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-sm text-primary-700 dark:text-primary-300 hover:underline truncate"
                >
                  {acceptedMeetingLink}
                </a>
                <Button size="sm" variant="outline" className="!rounded-lg" onClick={handleCopyLink} title="Copy link">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <Button variant="primary" className="w-full !rounded-xl" onClick={closeAcceptModal}>
                Done
              </Button>
            </>
          ) : (
            <>
              <h3
                id="accept-consultation-title"
                className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-2"
              >
                Accept consultation
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                Add your video call link (Zoom, Google Meet, Microsoft Teams, etc.). The client will
                receive it by email.
              </p>
              <div className="space-y-4">
                <Input
                  label="Meeting link"
                  type="url"
                  placeholder="https://zoom.us/j/... or meet.google.com/..."
                  value={meetingLink}
                  onChange={(e) => {
                    setMeetingLink(e.target.value);
                    if (meetingLinkError) setMeetingLinkError('');
                  }}
                  error={meetingLinkError}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
                  Paste a full link with https://, or enter zoom.us/... and we&apos;ll add https:// for
                  you.
                </p>
                <Input
                  label="Notes (optional)"
                  placeholder="Any notes for the client"
                  value={vendorNotes}
                  onChange={(e) => setVendorNotes(e.target.value)}
                />
              </div>
              <div className="flex gap-2 mt-6 justify-end">
                <Button variant="outline" className="!rounded-xl" onClick={closeAcceptModal}>
                  Cancel
                </Button>
                <Button
                  className="!rounded-xl"
                  onClick={() => handleAccept(showAcceptModal)}
                  loading={acceptMutation.isPending}
                  disabled={!meetingLink.trim() || acceptMutation.isPending}
                >
                  Accept &amp; send link
                </Button>
              </div>
            </>
          )}
        </div>
      </ModalShell>
    </div>
  );
};

export default VendorConsultations;
